import React, { useState, useEffect, useRef } from 'react'
import { Input, Typography, Card, Button, Spin, Avatar, Divider, message as antMessage } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPaperPlane, faTimes, faMinus, faRobot, faUser } from '@fortawesome/free-solid-svg-icons'
import { assistantInitiate, assistantContinue, assistantReadThread, assistantCheckRunStatus } from '../../utils'

const { TextArea } = Input
const { Text, Title } = Typography

const POLLING_INTERVAL = 1000 // 1 second
const STORAGE_KEY = 'ai_assistant_thread_id'

const ChatPopup = ({ onClose, JSONContext, assistantId }) => {
  const [isMinimized, setIsMinimized] = useState(false)
  const [message, setMessage] = useState('')
  const [messages, setMessages] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [threadId, setThreadId] = useState(null)
  const [runId, setRunId] = useState(null)
  const [error, setError] = useState(null)

  const messagesEndRef = useRef(null)
  const pollingRef = useRef(null)

  // Load thread_id from localStorage on component mount
  useEffect(() => {
    try {
      const savedThreadId = localStorage.getItem(STORAGE_KEY)
      if (savedThreadId && savedThreadId.startsWith('thread_')) {
        setThreadId(savedThreadId)

        // Load previous messages
        loadPreviousMessages(savedThreadId)
      }
    } catch (err) {
      console.error('Error loading thread ID from storage:', err)
    }
  }, [])

  // Save thread_id to localStorage when it changes
  useEffect(() => {
    if (threadId && threadId.startsWith('thread_')) {
      try {
        localStorage.setItem(STORAGE_KEY, threadId)
      } catch (err) {
        console.error('Error saving thread ID to storage:', err)
      }
    }
  }, [threadId])

  // Add this helper function at the top of the component
  const stripJSONContext = (message) => {
    return message.replace(/\[JSON_CONTEXT\].*?\[\/JSON_CONTEXT\]/s, '').trim()
  }

  // Load previous messages from the thread
  const loadPreviousMessages = async (threadId) => {
    if (!threadId || !threadId.startsWith('thread_')) return

    try {
      setIsLoading(true)

      const messagesResponse = await assistantReadThread(threadId)

      if (messagesResponse && messagesResponse.data && messagesResponse.data.data) {
        const loadedMessages = messagesResponse.data.data
          .map((msg) => ({
            role: msg.role,
            // Strip JSON context from loaded messages
            content: stripJSONContext(msg.content[0]?.text?.value || '')
          }))
          .reverse()

        setMessages(loadedMessages)
      }
    } catch (err) {
      console.error('Error loading previous messages:', err)
      setError(`Failed to load previous conversation: ${err.message}`)

      // If we can't load the previous thread, clear it
      setThreadId(null)
      localStorage.removeItem(STORAGE_KEY)
    } finally {
      setIsLoading(false)
    }
  }

  // Scroll to bottom of messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages])

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
      }
    }
  }, [])

  const handleSendMessage = async () => {
    if (!message.trim()) return

    try {
      setIsLoading(true)
      setError(null)

      // Extract user message, removing any JSON context wrapper if present
      const displayMessage = message.replace(/\[JSON_CONTEXT\].*?\[\/JSON_CONTEXT\]/s, '').trim()

      // Add user message to chat (only showing the actual message, not the JSON)
      const userMessage = { role: 'user', content: displayMessage }
      setMessages((prev) => [...prev, userMessage])

      // Clear input field
      const currentMessage = message.trim()
      setMessage('')

      if (!threadId) {
        try {
          // If we have JSONContext, wrap it in identifiers
          const messageWithContext = JSONContext
            ? `[JSON_CONTEXT]${
                typeof JSONContext === 'object' ? JSON.stringify(JSONContext) : JSONContext
              }[/JSON_CONTEXT]\n${currentMessage}`
            : currentMessage

          const response = await assistantInitiate(messageWithContext, assistantId)

          if (!response || !response.thread_id) {
            throw new Error('Failed to get a valid thread_id from the API')
          }

          setThreadId(response.thread_id)
          setRunId(response.id)

          // Start polling for response
          startPolling(response.thread_id, response.id)
        } catch (initError) {
          console.error('Error initiating thread:', initError)
          setError(`Failed to start conversation: ${initError.message}`)
          setIsLoading(false)
          return
        }
      } else {
        // Continue existing thread
        try {
          if (!threadId.startsWith('thread_')) {
            throw new Error(`Invalid thread_id format: ${threadId}`)
          }

          const response = await assistantContinue(currentMessage, threadId, assistantId)

          if (!response || !response.id) {
            throw new Error('Failed to get a valid run_id from the API')
          }

          setRunId(response.id)

          // Start polling for response
          startPolling(threadId, response.id)
        } catch (continueError) {
          console.error('Error continuing thread:', continueError)

          // If the thread is invalid or expired, start a new one
          if (
            continueError.message.includes('thread not found') ||
            continueError.message.includes('Invalid thread_id')
          ) {
            setThreadId(null)
            localStorage.removeItem(STORAGE_KEY)

            // Try again with a new thread
            setTimeout(() => {
              handleSendMessage()
            }, 500)
            return
          }

          setError(`Failed to continue conversation: ${continueError.message}`)
          setIsLoading(false)
          return
        }
      }
    } catch (err) {
      console.error('Error sending message:', err)
      setError(err.message || 'Failed to send message')
      setIsLoading(false)
    }
  }

  const startPolling = (threadId, runId) => {
    // Validate parameters
    if (!threadId || !threadId.startsWith('thread_')) {
      console.error('Invalid thread_id for polling:', threadId)
      setError(`Invalid thread_id: ${threadId}`)
      setIsLoading(false)
      return
    }

    if (!runId) {
      console.error('Invalid run_id for polling:', runId)
      setError('Invalid run_id')
      setIsLoading(false)
      return
    }

    // Clear any existing polling
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
    }

    // Start polling for run status
    pollingRef.current = setInterval(async () => {
      try {
        const statusResponse = await assistantCheckRunStatus(threadId, runId)

        if (statusResponse.status === 'completed') {
          // Run is complete, fetch messages
          clearInterval(pollingRef.current)

          try {
            const messagesResponse = await assistantReadThread(threadId)

            if (messagesResponse && messagesResponse.data && messagesResponse.data.data) {
              const newMessages = messagesResponse.data.data
                .filter(
                  (msg) =>
                    msg.role !== 'user' ||
                    messages.findIndex((m) => m.role === 'user' && m.content === msg.content) === -1
                )
                .map((msg) => ({
                  role: msg.role,
                  // Strip JSON context from new messages
                  content: stripJSONContext(msg.content[0]?.text?.value || '')
                }))
                .reverse()

              setMessages(newMessages)
            } else {
              console.warn('Received empty or invalid messages response:', messagesResponse)
            }
          } catch (readError) {
            console.error('Error reading thread messages:', readError)
            setError(`Failed to read messages: ${readError.message}`)
          }

          setIsLoading(false)
        } else if (
          statusResponse.status === 'failed' ||
          statusResponse.status === 'cancelled' ||
          statusResponse.status === 'expired'
        ) {
          // Run failed
          clearInterval(pollingRef.current)
          console.error(`Run ${statusResponse.status}:`, statusResponse)
          setError(`Assistant response ${statusResponse.status}`)
          setIsLoading(false)
        } else {
          // Still in progress
        }
        // For 'in_progress' or 'queued' status, continue polling
      } catch (err) {
        console.error('Error polling run status:', err)
        clearInterval(pollingRef.current)
        setError(err.message || 'Failed to get assistant response')
        setIsLoading(false)
      }
    }, POLLING_INTERVAL)
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized)
  }

  const handleClearConversation = () => {
    // Clear the thread ID from state and storage
    setThreadId(null)
    localStorage.removeItem(STORAGE_KEY)

    // Clear messages
    setMessages([])

    // Clear any errors
    setError(null)

    antMessage.success('Conversation history cleared')
  }

  return (
    <Card
      style={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        width: isMinimized ? 300 : 400,
        height: isMinimized ? 60 : 500,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        padding: 0
      }}
      bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      {/* Chat Header */}
      <div
        style={{
          background: '#1890ff',
          color: 'white',
          padding: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <FontAwesomeIcon icon={faRobot} style={{ marginRight: 8 }} />
          <Title level={5} style={{ margin: 0, color: 'white' }}>
            AI Assistant
          </Title>
        </div>
        <div>
          {!isMinimized && threadId && (
            <Button
              type='text'
              size='small'
              onClick={handleClearConversation}
              style={{ color: 'white', marginRight: 8 }}
              title='Clear conversation history'
            >
              New Chat
            </Button>
          )}
          <Button
            type='text'
            size='small'
            onClick={toggleMinimize}
            style={{ color: 'white', marginRight: 8 }}
            icon={<FontAwesomeIcon icon={faMinus} />}
          />
          <Button
            type='text'
            size='small'
            onClick={onClose}
            style={{ color: 'white' }}
            icon={<FontAwesomeIcon icon={faTimes} />}
          />
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages Area */}
          <div
            style={{
              flexGrow: 1,
              padding: 16,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              background: '#f5f5f5'
            }}
          >
            {messages.length === 0 ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Text type='secondary'>Send a message to start chatting with the AI assistant</Text>
              </div>
            ) : (
              messages.map((msg, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    marginBottom: 8
                  }}
                >
                  <div style={{ display: 'flex', maxWidth: '80%' }}>
                    {msg.role !== 'user' && (
                      <Avatar
                        style={{ backgroundColor: '#1890ff', marginRight: 8 }}
                        icon={<FontAwesomeIcon icon={faRobot} />}
                      />
                    )}
                    <Card
                      style={{
                        padding: 0,
                        borderRadius: 8,
                        background: msg.role === 'user' ? '#1890ff' : 'white',
                        color: msg.role === 'user' ? 'white' : 'rgba(0, 0, 0, 0.85)'
                      }}
                      bodyStyle={{ padding: '8px 12px' }}
                    >
                      <Text
                        style={{
                          whiteSpace: 'pre-wrap',
                          color: msg.role === 'user' ? 'white' : 'inherit'
                        }}
                      >
                        {msg.content}
                      </Text>
                    </Card>
                    {msg.role === 'user' && (
                      <Avatar
                        style={{ backgroundColor: '#52c41a', marginLeft: 8 }}
                        icon={<FontAwesomeIcon icon={faUser} />}
                      />
                    )}
                  </div>
                </div>
              ))
            )}
            {isLoading && (
              <div style={{ display: 'flex', justifyContent: 'center', margin: '16px 0' }}>
                <Spin size='small' />
              </div>
            )}
            {error && (
              <div style={{ padding: 16, background: '#fff1f0', borderRadius: 4, marginTop: 16 }}>
                <Text type='danger'>Error: {error}</Text>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <Divider style={{ margin: 0 }} />

          {/* Input Area */}
          <div style={{ padding: 16, display: 'flex', alignItems: 'center' }}>
            <TextArea
              placeholder='Type your message...'
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              autoSize={{ minRows: 1, maxRows: 3 }}
              style={{ marginRight: 8, flexGrow: 1 }}
            />
            <Button
              type='primary'
              onClick={handleSendMessage}
              disabled={!message.trim() || isLoading}
              icon={isLoading ? <Spin size='small' /> : <FontAwesomeIcon icon={faPaperPlane} />}
              style={{
                background: message.trim() ? '#1890ff' : '#d9d9d9',
                borderColor: message.trim() ? '#1890ff' : '#d9d9d9'
              }}
            />
          </div>
        </>
      )}
    </Card>
  )
}

export default ChatPopup
